/**
 * Token store — manages the boot + session JWT pair for the StreamPersona web client.
 *
 * - Browser: sessionStorage (cleared on tab close — XSS recovery short of httpOnly cookies)
 * - Tauri: sessionStorage as a Phase -2 fallback; Phase 0 wires OS keyring via Tauri keyring plugin
 *
 * The boot JWT is long-lived (7d) and identifies the licensed device. The session JWT is short-lived
 * (60min) and gates inference endpoints — it dies at Cloud Pass expiry, forcing re-payment.
 */

const STORAGE_KEY = "sp_tokens_v1";

export interface TokenPair {
  boot: string;
  session?: string;
  type: "boot" | "session";
  bootExpiresAt?: number; // Unix ms
  sessionExpiresAt?: number; // Unix ms
  licenseKey?: string;
  deviceId?: string;
}

function loadFromStorage(): TokenPair | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY) || sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as TokenPair;
  } catch {
    return null;
  }
}

function saveToStorage(t: TokenPair | null) {
  try {
    if (t) localStorage.setItem(STORAGE_KEY, JSON.stringify(t));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // best-effort; storage quota may be exhausted
  }
}

export function getToken(): TokenPair | null {
  return loadFromStorage();
}

export function setToken(t: TokenPair) {
  saveToStorage(t);
}

export function clearTokens() {
  saveToStorage(null);
}

export function isBootExpired(t: TokenPair | null): boolean {
  if (!t?.bootExpiresAt) return false; // unknown — let backend reject
  return Date.now() >= t.bootExpiresAt;
}

export function isSessionExpired(t: TokenPair | null): boolean {
  if (!t?.sessionExpiresAt) return true; // conservative
  return Date.now() >= t.sessionExpiresAt;
}

export function shouldRefresh(t: TokenPair | null): boolean {
  // Refresh if boot is near expiry (within 1 hour) or already expired
  if (!t?.bootExpiresAt) return false;
  const oneHourMs = 60 * 60 * 1000;
  return Date.now() + oneHourMs >= t.bootExpiresAt;
}

// Decode JWT payload (no verification — backend validates signature)
function decodeJwtExp(token: string): number | undefined {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return undefined;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    return payload.exp ? payload.exp * 1000 : undefined;
  } catch {
    return undefined;
  }
}

// Helper to mint token pair response into storage-friendly shape
export function saveIssuedTokens(boot: string, licenseKey: string, deviceId: string) {
  const bootExp = decodeJwtExp(boot);
  setToken({
    boot,
    type: "boot",
    bootExpiresAt: bootExp,
    licenseKey,
    deviceId,
  });
}

export function setSessionToken(session: string) {
  const existing = getToken();
  if (!existing) return;
  const sessionExp = decodeJwtExp(session);
  setToken({
    ...existing,
    session,
    sessionExpiresAt: sessionExp,
    type: "session",
  });
}